import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Apple, ArrowLeft, Check, Clipboard, ExternalLink, ListChecks, ShieldCheck } from 'lucide-react';
import './MyShoppingListJoinPage.css';

// Matches ShoppingList.makeCode() in the app: ten characters, no 0/O/1/I.
const SHARE_CODE_PATTERN = /^[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{10}$/;
const CANONICAL_JOIN_URL = 'https://hagidev-pages.web.app/list';
const APP_SCHEME_URL = 'myshoppinglist://join';

// Fill both in once the app is live; the download button stays hidden until then
// rather than pointing at a URL that 404s.
const APP_STORE_ID = '';
const APP_STORE_URL = APP_STORE_ID ? `https://apps.apple.com/il/app/id${APP_STORE_ID}` : '';

const PAGE_TITLE = 'הוזמנת לרשימת קניות משותפת';
const PAGE_DESCRIPTION = 'מישהו שיתף איתך רשימת קניות. פתח אותה באפליקציה כדי להוסיף פריטים ולראות עדכונים בזמן אמת.';

// Embedded webviews (WhatsApp/Instagram/Facebook/etc.) silently block
// custom-scheme navigation, so the app never opens. Detect them to guide the
// user to Safari — this is how most share links actually arrive.
const IN_APP_BROWSER_PATTERN = /(FBAN|FBAV|FB_IAB|Instagram|Messenger|WhatsApp|Line\/|Snapchat|Twitter|TikTok|MicroMessenger)/i;
const APP_OPEN_FALLBACK_MS = 1600;

const detectInAppBrowser = () =>
  typeof navigator !== 'undefined' && IN_APP_BROWSER_PATTERN.test(navigator.userAgent || '');

const trackEvent = (eventName, code) => {
  const detail = {
    share_code: code,
    source_path: window.location.pathname,
    timestamp: new Date().toISOString(),
  };

  window.dispatchEvent(new CustomEvent(eventName, { detail }));

  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, detail);
  }
};

const copyText = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand('copy');
  document.body.removeChild(textArea);
};

const JoinShell = ({ children }) => (
  <main className="sl-page" dir="rtl">
    <section className="sl-layout" aria-labelledby="sl-title">
      <article className="sl-card">{children}</article>
    </section>
  </main>
);

const MyShoppingListJoinPage = () => {
  const { shareCode: pathCode } = useParams();
  const location = useLocation();
  const [copied, setCopied] = useState(false);

  const shareCode = useMemo(() => {
    const queryCode = new URLSearchParams(location.search).get('code');
    return (pathCode || queryCode || '').trim().toUpperCase();
  }, [location.search, pathCode]);

  const isValidCode = SHARE_CODE_PATTERN.test(shareCode);
  const appUrl = `${APP_SCHEME_URL}/${encodeURIComponent(shareCode)}`;
  const canonicalUrl = isValidCode
    ? `${CANONICAL_JOIN_URL}/${encodeURIComponent(shareCode)}`
    : CANONICAL_JOIN_URL;

  const isInAppBrowser = useMemo(() => detectInAppBrowser(), []);

  useEffect(() => {
    trackEvent('shopping_list_join_landing_viewed', isValidCode ? shareCode : undefined);
  }, [isValidCode, shareCode]);

  useEffect(() => {
    const previousTitle = document.title;
    const metadata = [
      ['meta[name="description"]', PAGE_DESCRIPTION],
      ['meta[property="og:title"]', PAGE_TITLE],
      ['meta[property="og:description"]', PAGE_DESCRIPTION],
      ['meta[name="twitter:title"]', PAGE_TITLE],
      ['meta[name="twitter:description"]', PAGE_DESCRIPTION],
    ].map(([selector, nextContent]) => {
      const element = document.head.querySelector(selector);
      return { element, nextContent, previousContent: element?.getAttribute('content') };
    });

    document.title = PAGE_TITLE;
    metadata.forEach(({ element, nextContent }) => element?.setAttribute('content', nextContent));

    return () => {
      document.title = previousTitle;
      metadata.forEach(({ element, previousContent }) =>
        element?.setAttribute('content', previousContent || ''));
    };
  }, []);

  // Smart App Banner, so an iPhone that already has the app gets a native
  // "Open" affordance above the page.
  useEffect(() => {
    if (!isValidCode || !APP_STORE_ID) return undefined;

    const selector = 'meta[data-shopping-list-banner="true"]';
    const existing = document.head.querySelector(selector);
    const banner = existing || document.createElement('meta');
    banner.name = 'apple-itunes-app';
    banner.dataset.shoppingListBanner = 'true';
    banner.content = `app-id=${APP_STORE_ID}, app-argument=${canonicalUrl}`;
    if (!existing) document.head.appendChild(banner);

    return () => banner.remove();
  }, [canonicalUrl, isValidCode]);

  const handleCopy = async () => {
    try {
      await copyText(shareCode);
      setCopied(true);
      trackEvent('shopping_list_code_copied', shareCode);
      window.setTimeout(() => setCopied(false), 2200);
    } catch (error) {
      // The code stays selectable even if a browser denies clipboard access.
      console.error('Could not copy share code', error);
    }
  };

  const handleOpenApp = () => {
    trackEvent('shopping_list_open_app_tapped', shareCode);
    if (!isValidCode) return;

    // If the app takes over, the page is backgrounded and becomes hidden. If
    // that hasn't happened by the timeout, the scheme was blocked or the app
    // isn't installed — only then fall back to the App Store.
    let handedOff = false;
    const markHandedOff = () => {
      handedOff = true;
    };
    const onVisibilityChange = () => {
      if (document.hidden) markHandedOff();
    };

    document.addEventListener('visibilitychange', onVisibilityChange, { once: true });
    window.addEventListener('pagehide', markHandedOff, { once: true });

    window.setTimeout(() => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', markHandedOff);
      if (!handedOff && !document.hidden && APP_STORE_URL) {
        window.location.href = APP_STORE_URL;
      }
    }, APP_OPEN_FALLBACK_MS);

    window.location.href = appUrl;
  };

  if (!isValidCode) {
    return (
      <JoinShell>
        <div className="sl-invalid">
          <div className="sl-icon muted" aria-hidden="true">
            <ShieldCheck size={30} strokeWidth={2.25} />
          </div>
          <h1 id="sl-title">הקישור אינו תקין</h1>
          <p>בדוק את הקישור שקיבלת, או בקש שישתפו איתך את הרשימה מחדש.</p>
        </div>
      </JoinShell>
    );
  }

  return (
    <JoinShell>
      <div className="sl-icon" aria-hidden="true">
        <ListChecks size={30} strokeWidth={2.25} />
      </div>
      <p className="sl-eyebrow">רשימה משותפת</p>
      <h1 id="sl-title">הוזמנת לרשימת קניות</h1>
      <p className="sl-intro">
        מישהו שיתף איתך רשימת קניות. פתח אותה באפליקציה כדי להוסיף פריטים ולראות
        מה כבר נקנה — בזמן אמת, אצל כולם.
      </p>

      {isInAppBrowser && (
        <p className="sl-help sl-help--warn">
          אם הכפתור לא פותח את האפליקציה, פתח את הקישור ב-Safari (תפריט ⋯ ← "פתח ב-Safari") ונסה שוב.
        </p>
      )}

      <section className="sl-code-panel" aria-label="קוד הרשימה">
        <span>קוד הרשימה</span>
        <output className="sl-code" aria-label={`קוד רשימה ${shareCode}`}>{shareCode}</output>
        <button className="sl-copy" type="button" onClick={handleCopy}>
          {copied ? <Check size={18} /> : <Clipboard size={18} />}
          {copied ? 'הקוד הועתק' : 'העתק קוד'}
        </button>
      </section>

      <div className="sl-actions">
        <a
          className="sl-primary"
          href={appUrl}
          onClick={(event) => {
            event.preventDefault();
            handleOpenApp();
          }}
        >
          <ExternalLink size={18} />
          <span>פתח את הרשימה באפליקציה</span>
        </a>

        {APP_STORE_URL && (
          <a
            className="sl-secondary"
            href={APP_STORE_URL}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackEvent('shopping_list_app_store_tapped', shareCode)}
          >
            <Apple size={19} />
            <span>הורד מ-<bdi>App Store</bdi></span>
            <ArrowLeft className="sl-arrow" size={17} />
          </a>
        )}
      </div>

      <p className="sl-help">
        אין לך את האפליקציה? אחרי ההתקנה חזור לקישור הזה, או הזן את הקוד ידנית במסך הצטרפות לרשימה.
      </p>
    </JoinShell>
  );
};

export default MyShoppingListJoinPage;
